import { SPEC_FILE_NAME } from "@/constants";
import { useLatestVersionNumber } from "@/lib/hooks/useVersionInformation";
import { useMessageStore } from "@/state/stores";
import { useMemo } from "react";
import type { FpMessage } from "../../../../../schemas/codegen";

function generateFileLinkProps(
  chatId: string | undefined,
  path: string,
  versionNumber: number | undefined,
) {
  if (!chatId || !versionNumber) {
    return null;
  }

  if (path === SPEC_FILE_NAME) {
    return {
      to: "/codegen/$chatId/attachments/$versionNumber" as const,
      params: {
        chatId,
        versionNumber: `v${versionNumber || 1}`,
      },
      search: { tab: "spec" as const },
    };
  }

  return {
    to: "/codegen/$chatId/attachments/$versionNumber" as const,
    params: {
      chatId,
      versionNumber: `v${versionNumber}`,
    },
    search: { tab: "code" as const, file: path },
  };
}

export function useFileLinkProps(
  chatId: string | undefined,
  messageId: string,
  path: string,
) {
  const latestVersionNumber = useLatestVersionNumber();
  const closestVersionNumber =
    useMessageStore((state) => {
      if (!chatId) {
        return;
      }

      return findClosestVersionNumber(state.messages, messageId, path);
    }) ?? latestVersionNumber;

  return useMemo(() => {
    if (closestVersionNumber === -1) {
      return null;
    }

    return generateFileLinkProps(chatId, path || "", closestVersionNumber);
  }, [chatId, path, closestVersionNumber]);
}

function findClosestVersionNumber(
  messages: FpMessage[],
  messageId: string,
  path: string,
) {
  const currentMessage = messages.find((m) => m.id === messageId);
  if (!currentMessage || currentMessage.status === "cancelled") {
    // Return -1 to indicate that we can't reliably find the version number
    // TODO: implement a better version that allows to find the version number in certain cases
    // I can imagine that a Read action would sort of be ok (as it can link to file from a previous version)
    // I guess we could rely on whether the message has a version (and only then return -1) but I'm not sure
    // if that's always a solid solution
    return -1;
  }

  // Look backwards through messages to find the closest version
  const currentMessageIndex = messages.findIndex((m) => m.id === messageId);

  let encounteredVersions = false;
  // Start from the current message and go backwards
  for (let i = currentMessageIndex; i >= 0; i--) {
    const message = messages[i];
    if (message.version && message.status !== "cancelled") {
      encounteredVersions = true;
      // Check if this version contains the file we're typechecking
      const file =
        path === SPEC_FILE_NAME
          ? message.spec
          : message.version.files.find((f) => f.filename === path);

      if (file) {
        return message.version.version;
      }
    }
  }

  // If no version found in the message and the ones before it, assume its the first version
  if (path === SPEC_FILE_NAME && encounteredVersions === false) {
    return 1;
  }
}
